import React, { FormEvent, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import api, { OPERATING_GROUP_CHANGED_EVENT, OPERATING_GROUP_STORAGE_KEY } from '../../api/api';
import { useAppSelector } from '../../store/hooks';
import { isOperatingContextReady, useOperatingContext } from '../../utils/operatingContextLifecycle';
import {
  OPERATING_GROUP_CHANGED_IN_ANOTHER_TAB_MESSAGE,
  useSelectedOperatingGroup,
} from '../../hooks/useSelectedOperatingGroup';
import './AuditLogsPage.css';

interface AuditLogEntry {
  id: number;
  occurredAtUtc: string;
  userId?: string | null;
  username: string;
  method: string;
  path?: string;
  controller: string;
  action: string;
  entityId?: string | null;
  statusCode: number;
  succeeded: boolean;
  durationMs: number;
  ipAddress?: string;
  correlationId?: string;
  operatingGroupId?: number | null;
  operatingGroupName?: string | null;
  resourceKind?: string | null;
  resourceId?: number | null;
  resourceLabel?: string;
}

interface AuditLogResponse {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  items: AuditLogEntry[];
}

interface AuditRestaurant {
  id: number;
  name: string;
  isArchived: boolean;
}

interface AuditFilters {
  username: string;
  controller: string;
  method: string;
  result: string;
  from: string;
  to: string;
}

const emptyFilters: AuditFilters = {
  username: '',
  controller: '',
  method: '',
  result: '',
  from: '',
  to: '',
};

const toUtc = (value: string, endOfDay = false) => {
  if (!value) return undefined;
  const local = new Date(`${value}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}`);
  return local.toISOString();
};

const formatDate = (value: string) => new Date(value).toLocaleString('uk-UA');

const AuditLogsPage: React.FC = () => {
  const token = useAppSelector(state => state.auth.token);
  const context = useOperatingContext();
  const contextReady = isOperatingContextReady(context, token);
  const systemWide = context.status === 'all' && context.canViewAllGroups;
  const readsEnabled = systemWide || context.restaurantAuditReadsEnabled;
  const { groupId, changedInAnotherTab } = useSelectedOperatingGroup();
  const [data, setData] = useState<AuditLogResponse | null>(null);
  const [filters, setFilters] = useState<AuditFilters>(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState<AuditFilters>(emptyFilters);
  const [restaurants, setRestaurants] = useState<AuditRestaurant[]>([]);
  const [restaurantFilter, setRestaurantFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [restaurantsLoading, setRestaurantsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restaurantsError, setRestaurantsError] = useState<string | null>(null);
  const [selected, setSelected] = useState<AuditLogEntry | null>(null);
  const [detail, setDetail] = useState<AuditLogEntry | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [contextRevision, setContextRevision] = useState(0);
  const [queryRevision, setQueryRevision] = useState(0);
  const [blockedByCrossTab, setBlockedByCrossTab] = useState(false);
  const requestSequence = useRef(0);
  const detailSequence = useRef(0);
  const restaurantSequence = useRef(0);
  const activeRequest = useRef<AbortController | null>(null);
  const activeDetailRequest = useRef<AbortController | null>(null);
  const activeRestaurantRequest = useRef<AbortController | null>(null);
  const crossTabBlockRef = useRef(false);

  const clearDetails = () => {
    detailSequence.current += 1;
    activeDetailRequest.current?.abort();
    activeDetailRequest.current = null;
    setSelected(null);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(false);
  };

  const invalidateAuditRequest = () => {
    requestSequence.current += 1;
    activeRequest.current?.abort();
    activeRequest.current = null;
  };

  useEffect(() => {
    const invalidate = () => {
      invalidateAuditRequest();
      clearDetails();
      setData(null);
      setLoading(false);
      setError(null);
      setRestaurants([]);
      setRestaurantFilter('all');
      setPage(1);
      setContextRevision(revision => revision + 1);
    };
    const operatingGroupChanged = () => invalidate();
    const storageChanged = (event: StorageEvent) => {
      if (event.key !== OPERATING_GROUP_STORAGE_KEY) return;
      crossTabBlockRef.current = true;
      setBlockedByCrossTab(true);
      invalidate();
    };
    window.addEventListener(OPERATING_GROUP_CHANGED_EVENT, operatingGroupChanged);
    window.addEventListener('storage', storageChanged);
    return () => {
      window.removeEventListener(OPERATING_GROUP_CHANGED_EVENT, operatingGroupChanged);
      window.removeEventListener('storage', storageChanged);
      activeRequest.current?.abort();
      activeDetailRequest.current?.abort();
      activeRestaurantRequest.current?.abort();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const sequence = ++restaurantSequence.current;
    activeRestaurantRequest.current?.abort();
    activeRestaurantRequest.current = null;
    setRestaurants([]);
    setRestaurantsError(null);
    if (!contextReady || !systemWide || changedInAnotherTab || crossTabBlockRef.current) {
      setRestaurantsLoading(false);
      return () => { cancelled = true; };
    }

    const controller = new AbortController();
    activeRestaurantRequest.current = controller;
    setRestaurantsLoading(true);
    api.get<AuditRestaurant[]>('/audit-logs/restaurants', { signal: controller.signal })
      .then(response => {
        if (!cancelled && sequence === restaurantSequence.current && !crossTabBlockRef.current)
          setRestaurants(response.data);
      })
      .catch(() => {
        if (!cancelled && sequence === restaurantSequence.current && !crossTabBlockRef.current)
          setRestaurantsError('Не вдалося завантажити перелік ресторанів.');
      })
      .finally(() => {
        if (!cancelled && sequence === restaurantSequence.current) setRestaurantsLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
      if (activeRestaurantRequest.current === controller) activeRestaurantRequest.current = null;
    };
  }, [changedInAnotherTab, context.generation, contextReady, contextRevision, context.status, systemWide]);

  useEffect(() => {
    let cancelled = false;
    const sequence = ++requestSequence.current;
    if (!contextReady || !readsEnabled || changedInAnotherTab || crossTabBlockRef.current) {
      setData(null);
      setLoading(false);
      setError(null);
      return () => { cancelled = true; };
    }
    const controller = new AbortController();
    activeRequest.current = controller;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const params: Record<string, string | number | boolean> = { page, pageSize: 50 };
        if (appliedFilters.username.trim()) params.username = appliedFilters.username.trim();
        if (appliedFilters.controller.trim()) params.controller = appliedFilters.controller.trim();
        if (appliedFilters.method) params.method = appliedFilters.method;
        if (appliedFilters.result) params.succeeded = appliedFilters.result === 'success';
        const fromUtc = toUtc(appliedFilters.from);
        const toUtcValue = toUtc(appliedFilters.to, true);
        if (fromUtc) params.fromUtc = fromUtc;
        if (toUtcValue) params.toUtc = toUtcValue;
        if (systemWide && restaurantFilter === 'unassigned') params.unassigned = true;
        else if (systemWide && restaurantFilter !== 'all') params.operatingGroupId = Number(restaurantFilter);

        const response = await api.get<AuditLogResponse>('/audit-logs', {
          params,
          signal: controller.signal,
        });
        if (!cancelled && sequence === requestSequence.current && !crossTabBlockRef.current)
          setData(response.data);
      } catch (requestError) {
        if (!cancelled && sequence === requestSequence.current && !crossTabBlockRef.current) {
          const deniedGroupRead = groupId && axios.isAxiosError(requestError) &&
            requestError.response?.status === 403;
          setError(deniedGroupRead
            ? 'Немає доступу до журналу аудиту в цьому ресторані.'
            : 'Не вдалося завантажити журнал аудиту.');
        }
      } finally {
        if (!cancelled && sequence === requestSequence.current) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      controller.abort();
      if (activeRequest.current === controller) activeRequest.current = null;
    };
  }, [
    appliedFilters,
    changedInAnotherTab,
    context.generation,
    contextReady,
    context.status,
    contextRevision,
    groupId,
    page,
    queryRevision,
    restaurantFilter,
    readsEnabled,
    systemWide,
  ]);

  if (blockedByCrossTab || changedInAnotherTab) {
    return (
      <div className="page-shell audit-logs-page">
        <h2>Журнал аудиту</h2>
        <p role="alert" className="audit-alert">{OPERATING_GROUP_CHANGED_IN_ANOTHER_TAB_MESSAGE}</p>
      </div>
    );
  }

  if (contextReady && !readsEnabled) {
    return (
      <div className="page-shell audit-logs-page">
        <h2>Журнал аудиту</h2>
        <p role="status">Журнал аудиту ресторану ще не активовано.</p>
      </div>
    );
  }

  if (!contextReady) {
    return (
      <div className="page-shell audit-logs-page">
        <h2>Журнал аудиту</h2>
        <p role="status">Підтвердження операційного контексту…</p>
      </div>
    );
  }

  // Each explicit query action clears the visible result, so it must always trigger a fresh
  // request even when the resulting filter, page and restaurant values are unchanged.
  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    invalidateAuditRequest();
    clearDetails();
    setData(null);
    setPage(1);
    setAppliedFilters({ ...filters });
    setQueryRevision(revision => revision + 1);
  };

  const resetFilters = () => {
    invalidateAuditRequest();
    clearDetails();
    setData(null);
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    if (systemWide) setRestaurantFilter('all');
    setPage(1);
    setQueryRevision(revision => revision + 1);
  };

  const chooseRestaurant = (value: string) => {
    invalidateAuditRequest();
    clearDetails();
    setData(null);
    setError(null);
    setRestaurantFilter(value);
    setPage(1);
    setQueryRevision(revision => revision + 1);
  };

  const showDetails = async (item: AuditLogEntry) => {
    clearDetails();
    const sequence = ++detailSequence.current;
    const controller = new AbortController();
    activeDetailRequest.current = controller;
    setSelected(item);
    setDetailLoading(true);
    try {
      const response = await api.get<AuditLogEntry>(`/audit-logs/${item.id}`, {
        signal: controller.signal,
      });
      if (sequence === detailSequence.current && !crossTabBlockRef.current)
        setDetail(response.data);
    } catch (requestError) {
      if (sequence === detailSequence.current && !crossTabBlockRef.current) {
        setDetailError(axios.isAxiosError(requestError) && requestError.response?.status === 404
          ? 'Запис недоступний або не знайдений.'
          : 'Не вдалося завантажити деталі запису.');
      }
    } finally {
      if (sequence === detailSequence.current) setDetailLoading(false);
    }
  };

  const selectedRestaurant = systemWide && restaurantFilter !== 'all'
    ? restaurantFilter === 'unassigned'
      ? 'Без ресторану'
      : restaurants.find(restaurant => restaurant.id === Number(restaurantFilter))?.name ?? 'Обраний ресторан'
    : null;

  const detailFields = detail
    ? [
      ['Час', formatDate(detail.occurredAtUtc)],
      ['Користувач', detail.username],
      ...(systemWide ? [
        ['ID користувача', detail.userId || '—'],
        ['Операція', `${detail.method} ${detail.path || '—'}`],
        ['ID об’єкта', detail.entityId || '—'],
        ['Ресторан', detail.operatingGroupId == null ? 'Без ресторану' : detail.operatingGroupName || `#${detail.operatingGroupId}`],
        ['Тип ресурсу', detail.resourceKind || 'Ресурс не класифіковано'],
        ['ID ресурсу', detail.resourceId == null ? '—' : String(detail.resourceId)],
        ['IP-адреса', detail.ipAddress || '—'],
        ['Correlation ID', detail.correlationId || '—'],
      ] : [
        ['Операція', `${detail.method} ${detail.controller}.${detail.action}`],
        ['Ресурс', detail.resourceLabel || '—'],
      ]),
      ['Обробник', `${detail.controller}.${detail.action}`],
      ['HTTP статус', String(detail.statusCode)],
      ['Тривалість', `${detail.durationMs} мс`],
    ]
    : [];

  return (
    <div className="page-shell audit-logs-page">
      <header className="audit-page-header">
        <h2>Журнал аудиту</h2>
        <p>Історія операцій без збереження паролів, токенів або вмісту запитів.</p>
      </header>

      <div className="audit-notice" role="note">
        {systemWide
          ? 'Системний огляд містить повні аудиторські метадані. Використовуйте фільтр, щоб переглянути окремий ресторан або записи без прив’язки до ресторану.'
          : 'Показано лише типізовані операції з ресурсами цього ресторану, до яких у вас є поточний доступ. IP-адреси, ідентифікатори користувачів, шлях запиту та технічні correlation ID приховані.'}
      </div>

      <form className="audit-filters" onSubmit={applyFilters}>
        {systemWide && (
          <label className="audit-field">
            Ресторан
            <select
              aria-label="Ресторан"
              value={restaurantFilter}
              onChange={event => chooseRestaurant(event.target.value)}
              disabled={restaurantsLoading}
            >
              <option value="all">Усі ресторани</option>
              <option value="unassigned">Без ресторану</option>
              {restaurants.map(restaurant => (
                <option key={restaurant.id} value={String(restaurant.id)}>
                  {restaurant.name}{restaurant.isArchived ? ' (архівний)' : ''}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="audit-field">
          Користувач
          <input
            value={filters.username}
            onChange={event => setFilters(current => ({ ...current, username: event.target.value }))}
            placeholder="Ім’я користувача"
          />
        </label>
        <label className="audit-field">
          Контролер
          <input
            value={filters.controller}
            onChange={event => setFilters(current => ({ ...current, controller: event.target.value }))}
            placeholder="Products"
          />
        </label>
        <label className="audit-field">
          Метод
          <select value={filters.method} onChange={event => setFilters(current => ({ ...current, method: event.target.value }))}>
            <option value="">Усі</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="PATCH">PATCH</option>
            <option value="DELETE">DELETE</option>
          </select>
        </label>
        <label className="audit-field">
          Результат
          <select value={filters.result} onChange={event => setFilters(current => ({ ...current, result: event.target.value }))}>
            <option value="">Усі</option>
            <option value="success">Успішні</option>
            <option value="failure">Помилки</option>
          </select>
        </label>
        <label className="audit-field">
          Від
          <input type="date" value={filters.from} onChange={event => setFilters(current => ({ ...current, from: event.target.value }))} />
        </label>
        <label className="audit-field">
          До
          <input type="date" value={filters.to} onChange={event => setFilters(current => ({ ...current, to: event.target.value }))} />
        </label>
        <div className="audit-filter-actions">
          <button type="submit" className="audit-button audit-button-primary" disabled={loading}>Застосувати</button>
          <button type="button" className="audit-button" onClick={resetFilters} disabled={loading}>Скинути</button>
        </div>
      </form>

      {restaurantsError && <p role="alert" className="audit-alert">{restaurantsError}</p>}
      {error && <p role="alert" className="audit-alert">{error}</p>}
      <div className="audit-summary" aria-live="polite">
        {loading ? 'Завантаження журналу…' : data ? `Знайдено записів: ${data.total}` : ' '}
        {selectedRestaurant && <span>Фільтр: {selectedRestaurant}</span>}
      </div>

      <section className="audit-list" aria-label="Записи журналу аудиту" aria-busy={loading}>
        {loading && <p role="status" className="audit-empty">Завантаження…</p>}
        {!loading && data?.items?.length === 0 && <p className="audit-empty">Записів не знайдено.</p>}
        {!loading && data?.items?.map(item => {
          const unclassified = systemWide && item.operatingGroupId != null &&
            (!item.resourceKind || item.resourceId == null || item.resourceId <= 0);
          const restaurantName = item.operatingGroupId == null
            ? 'Без ресторану'
            : item.operatingGroupName || `Ресторан #${item.operatingGroupId}`;
          return (
            <article className="audit-entry" key={item.id}>
              <div className="audit-entry-main">
                <div className="audit-entry-heading">
                  <span className={`audit-method audit-method-${item.method.toLowerCase()}`}>{item.method}</span>
                  <strong>{item.controller}.{item.action}</strong>
                </div>
                <dl className="audit-entry-fields">
                  <div><dt>Дата й час</dt><dd>{formatDate(item.occurredAtUtc)}</dd></div>
                  <div><dt>Користувач</dt><dd>{item.username}</dd></div>
                  <div><dt>Ресурс</dt><dd>{item.resourceLabel || item.entityId || '—'}</dd></div>
                  {systemWide && <div><dt>Ресторан</dt><dd>{restaurantName}</dd></div>}
                  <div>
                    <dt>Результат</dt>
                    <dd className={item.succeeded ? 'audit-result-success' : 'audit-result-failure'}>
                      {item.succeeded ? 'Успішно' : `Помилка ${item.statusCode}`}
                    </dd>
                  </div>
                  <div><dt>Тривалість</dt><dd>{item.durationMs} мс</dd></div>
                </dl>
                {unclassified && <span className="audit-unclassified">Ресурс не класифіковано</span>}
              </div>
              <button type="button" className="audit-button audit-details-button" onClick={() => showDetails(item)}>
                Деталі
              </button>
            </article>
          );
        })}
      </section>

      {data && data.totalPages > 1 && (
        <nav className="audit-pagination" aria-label="Пагінація журналу аудиту">
          <button
            type="button"
            className="audit-button"
            aria-label="Попередня сторінка"
            disabled={page <= 1 || loading}
            onClick={() => { invalidateAuditRequest(); clearDetails(); setData(null); setPage(current => current - 1); }}
          >
            ← Назад
          </button>
          <span>{page} / {data.totalPages}</span>
          <button
            type="button"
            className="audit-button"
            aria-label="Наступна сторінка"
            disabled={page >= data.totalPages || loading}
            onClick={() => { invalidateAuditRequest(); clearDetails(); setData(null); setPage(current => current + 1); }}
          >
            Далі →
          </button>
        </nav>
      )}

      {selected && (
        <div className="audit-details-overlay" role="presentation" onClick={clearDetails}>
          <section
            className="audit-details-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="audit-details-title"
            onClick={event => event.stopPropagation()}
          >
            <div className="audit-details-header">
              <h3 id="audit-details-title">Запис аудиту #{selected.id}</h3>
              <button type="button" className="audit-close-button" aria-label="Закрити" onClick={clearDetails}>×</button>
            </div>
            {detailLoading && <p role="status">Завантаження деталей…</p>}
            {detailError && <p role="alert" className="audit-alert">{detailError}</p>}
            {detail && (
              <dl className="audit-details-list">
                {detailFields.map(([label, value]) => (
                  <React.Fragment key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </React.Fragment>
                ))}
              </dl>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default AuditLogsPage;
